import React, { useState } from "react";
import { collection, addDoc, serverTimestamp } from "firebase/firestore";
import { firestore } from "../services/firebase";
import "./BatchOperationModal.css";

interface BatchOperationModalProps {
  selectedArtworkIds: string[];
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

const BatchOperationModal: React.FC<BatchOperationModalProps> = ({
  selectedArtworkIds,
  isOpen,
  onClose,
  onSuccess,
}) => {
  const [operationType, setOperationType] = useState("call");
  const [description, setDescription] = useState("");
  const [outcome, setOutcome] = useState(""); // ✅ NUOVO CAMPO
  const [nextAction, setNextAction] = useState(""); // ✅ NUOVO CAMPO
  const [notes, setNotes] = useState(""); // ✅ NUOVO CAMPO
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!description.trim()) {
      setError("Description is required");
      return;
    }

    if (selectedArtworkIds.length === 0) {
      setError("No artworks selected");
      return;
    }

    setLoading(true);
    setError(null);

    try {
      console.log(
        "🔄 Starting batch operation for",
        selectedArtworkIds.length,
        "artworks"
      );

      // Crea un batch ID unico
      const batchId = `batch_${Date.now()}`;
      const currentDate = new Date();

      // Aggiungi l'operazione alla subcollection di ogni artwork
      const promises = selectedArtworkIds.map(async (artworkId, index) => {
        try {
          console.log(
            `📝 [${index + 1}/${
              selectedArtworkIds.length
            }] Adding operation to artwork:`,
            artworkId
          );

          const operationsRef = collection(
            firestore,
            "artworks",
            artworkId,
            "operations"
          );

          const docData = {
            type: operationType,
            description: description.trim(),
            outcome: outcome.trim() || "", // ✅ NUOVO CAMPO
            nextAction: nextAction.trim() || "", // ✅ NUOVO CAMPO
            nextActionStatus: nextAction.trim() ? "pending" : undefined, // ✅ NUOVO CAMPO
            notes: notes.trim() || "", // ✅ NUOVO CAMPO
            date: serverTimestamp(), // Usa 'date' invece di 'timestamp' per consistenza
            createdAt: serverTimestamp(),
            updatedAt: serverTimestamp(),
            batchId: batchId, // Per tracciare le operazioni batch
          };

          console.log(
            `📝 [${index + 1}/${selectedArtworkIds.length}] Operation data:`,
            docData
          );

          const docRef = await addDoc(operationsRef, docData);

          console.log(
            `✅ [${index + 1}/${
              selectedArtworkIds.length
            }] Operation added with ID:`,
            docRef.id
          );

          return {
            artworkId,
            operationId: docRef.id,
            success: true,
          };
        } catch (operationError) {
          console.error(
            `❌ Error adding operation to artwork ${artworkId}:`,
            operationError
          );
          return {
            artworkId,
            operationId: null,
            success: false,
            error: operationError,
          };
        }
      });

      const results = await Promise.all(promises);

      // Conta successi e fallimenti
      const successful = results.filter((r) => r.success).length;
      const failed = results.filter((r) => !r.success).length;

      console.log(
        `📊 Batch operation completed: ${successful} successful, ${failed} failed`
      );

      if (failed > 0) {
        const failedArtworks = results
          .filter((r) => !r.success)
          .map((r) => r.artworkId);
        console.warn("❌ Failed artworks:", failedArtworks);

        if (successful > 0) {
          alert(
            `Partially successful: ${successful} operations added, ${failed} failed. Check console for details.`
          );
        } else {
          setError(
            `Failed to add operations to all ${failed} artworks. Please try again.`
          );
          return;
        }
      } else {
        // Messaggio di successo personalizzato
        let successMessage = `✅ Successfully added ${successful} operations!`;

        if (nextAction.trim()) {
          successMessage += `\n📋 Next actions created for all selected artworks.`;
        }

        alert(successMessage);
      }

      // Reset form
      setDescription("");
      setOutcome("");
      setNextAction("");
      setNotes("");
      setError(null);

      onSuccess();
    } catch (error) {
      console.error("❌ Error in batch operation:", error);
      if (error && typeof error === "object") {
        setError(`Error adding operations: ${(error as any).message || error}`);
      } else {
        setError(`Error adding operations: ${String(error)}`);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      setDescription("");
      setOutcome("");
      setNextAction("");
      setNotes("");
      setError(null);
      onClose();
    }
  };

  // Calcola se ci sono next actions
  const hasNextActions = nextAction.trim().length > 0;

  if (!isOpen) return null;

  return (
    <div className="batch-modal-overlay" onClick={handleClose}>
      <div
        className="batch-modal-container"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="batch-modal-header">
          <div className="batch-modal-header-content">
            <div className="batch-modal-icon">
              <span>📝</span>
            </div>
            <div>
              <h2 className="batch-modal-title">Add Batch Operation</h2>
              <p className="batch-modal-subtitle">
                Add operation to {selectedArtworkIds.length} selected artwork
                {selectedArtworkIds.length !== 1 ? "s" : ""}
              </p>
            </div>
          </div>
        </div>

        {/* Error Message */}
        {error && (
          <div className="batch-modal-error">
            <span className="batch-modal-error-icon">⚠️</span>
            {error}
          </div>
        )}

        {/* Form */}
        <form className="batch-modal-form" onSubmit={handleSubmit}>
          <div className="batch-modal-form-group">
            <label className="batch-modal-label">📞 Operation Type:</label>
            <select
              className="batch-modal-select"
              value={operationType}
              onChange={(e) => setOperationType(e.target.value)}
              disabled={loading}
            >
              <option value="call">📞 Phone Call</option>
              <option value="email">📧 Email</option>
              <option value="meeting">🤝 Meeting</option>
              <option value="visit">🏛️ Site Visit</option>
              <option value="status_change">🔄 Status Change</option>
              <option value="note_added">📝 Note Added</option>
              <option value="digitization">📷 Digitization</option>
              <option value="research">🔍 Research</option>
              <option value="other">🔧 Other</option>
            </select>
          </div>

          <div className="batch-modal-form-group">
            <label className="batch-modal-label">📝 Description:</label>
            <textarea
              className="batch-modal-textarea"
              value={description}
              onChange={(e) => {
                setDescription(e.target.value);
                setError(null);
              }}
              placeholder="Describe what was done, discussed, or planned..."
              required
              disabled={loading}
              rows={3}
            />
          </div>

          {/* ✅ NUOVO CAMPO: Outcome */}
          <div className="batch-modal-form-group">
            <label className="batch-modal-label">🎯 Outcome:</label>
            <textarea
              className="batch-modal-textarea"
              value={outcome}
              onChange={(e) => setOutcome(e.target.value)}
              placeholder="What was the result or outcome of this operation?"
              disabled={loading}
              rows={2}
            />
          </div>

          {/* ✅ NUOVO CAMPO: Next Action */}
          <div className="batch-modal-form-group">
            <label className="batch-modal-label">
              ⏭️ Next Action:
              <span className="batch-modal-label-optional">(Optional)</span>
            </label>
            <textarea
              className="batch-modal-textarea"
              value={nextAction}
              onChange={(e) => setNextAction(e.target.value)}
              placeholder="What needs to be done next? (This will create a next action for each artwork)"
              disabled={loading}
              rows={2}
            />
            {hasNextActions && (
              <div className="batch-modal-next-action-info">
                <span className="batch-modal-next-action-icon">📋</span>
                <span className="batch-modal-next-action-text">
                  This will create {selectedArtworkIds.length} pending next
                  action{selectedArtworkIds.length !== 1 ? "s" : ""}
                </span>
              </div>
            )}
          </div>

          {/* ✅ NUOVO CAMPO: Notes */}
          <div className="batch-modal-form-group">
            <label className="batch-modal-label">
              📄 Additional Notes:
              <span className="batch-modal-label-optional">(Optional)</span>
            </label>
            <textarea
              className="batch-modal-textarea"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Any additional notes or comments..."
              disabled={loading}
              rows={2}
            />
          </div>

          {/* Selected Artworks Preview */}
          <div className="batch-modal-preview">
            <div className="batch-modal-preview-container">
              <div className="batch-modal-preview-header">
                <span className="batch-modal-preview-icon">📋</span>
                <strong className="batch-modal-preview-title">
                  Batch Operation Summary:
                </strong>
              </div>
              <div className="batch-modal-preview-content">
                <div className="batch-modal-preview-item">
                  <span className="batch-modal-preview-item-icon">🎨</span>
                  <span className="batch-modal-preview-item-text">
                    <strong>{selectedArtworkIds.length}</strong> artwork
                    {selectedArtworkIds.length !== 1 ? "s" : ""} selected
                  </span>
                </div>
                <div className="batch-modal-preview-item">
                  <span className="batch-modal-preview-item-icon">📝</span>
                  <span className="batch-modal-preview-item-text">
                    Operation type:{" "}
                    <strong>{operationType.replace("_", " ")}</strong>
                  </span>
                </div>
                {hasNextActions && (
                  <div className="batch-modal-preview-item">
                    <span className="batch-modal-preview-item-icon">⏭️</span>
                    <span className="batch-modal-preview-item-text">
                      <strong>{selectedArtworkIds.length}</strong> next action
                      {selectedArtworkIds.length !== 1 ? "s" : ""} will be
                      created
                    </span>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="batch-modal-actions">
            <button
              type="button"
              className="batch-modal-button batch-modal-button-cancel"
              onClick={handleClose}
              disabled={loading}
            >
              Cancel
            </button>

            <button
              type="submit"
              className="batch-modal-button batch-modal-button-submit"
              disabled={loading || !description.trim()}
            >
              {loading ? (
                <>
                  <div className="batch-modal-spinner" />
                  Adding...
                </>
              ) : (
                <>
                  <span>✅</span>
                  Add to {selectedArtworkIds.length} Artwork
                  {selectedArtworkIds.length !== 1 ? "s" : ""}
                  {hasNextActions && (
                    <span className="batch-modal-button-next-action-indicator">
                      + Next Actions
                    </span>
                  )}
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default BatchOperationModal;
